import React, { FC, useEffect, useState } from "react";
import { Table, notification } from "antd";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  useDeleteReportCategoriesMutation,
  useGetReportCategoriesQuery,
  useUpdateReportCategoriesMutation,
} from "shared/api";
import { handleRequestError } from "shared/helpers";
import { useConfirmDeleteModal } from "shared/hooks";
import { useGetReportCategoriesColumns } from "shared/hooks/useGetReportCategorielsColumns";
import type { ColumnsType } from "antd/es/table";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { DraggableRow } from "./DraggableRow";

interface ReportCategory {
  id: number;
  name: string;
  order?: number;
}

interface ReportCategoriesTableProps {
  showEditModal: () => void;
}

const ReportCategoriesTable: FC<ReportCategoriesTableProps> = ({
  showEditModal,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const [deleteReportCategory] = useDeleteReportCategoriesMutation();
  const [updateReportCategory] = useUpdateReportCategoriesMutation();
  const { setValues } = useFormikContext<{ id: number; name: string }>();
  const { data: reportCategories = [] } = useGetReportCategoriesQuery({});

  const [items, setItems] = useState<ReportCategory[]>([]);

  useEffect(() => {
    setItems(
      [...reportCategories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
  }, [reportCategories]);

  const handleEditReport = (record: ReportCategory) => {
    showEditModal();
    setValues(record);
  };

  const handleDeleteReportCategory = async (report: ReportCategory) => {
    try {
      await deleteReportCategory(report).unwrap();
      api.success({
        message: t("reports.success-title"),
        description: t("Report category successfully deleted"),
      });
    } catch (error) {
      api.error({
        message: t("reports.error-title"),
        description: handleRequestError(error, t("reports.error")),
      });
    }
  };

  const showDeleteReportCategoryModal = useConfirmDeleteModal(
    t("Are you sure you want to delete this report category?"),
    handleDeleteReportCategory
  );

  const columns = useGetReportCategoriesColumns({
    handleEditReport,
    showDeleteReportCategoryModal,
  }) as ColumnsType<ReportCategory>;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    try {
      await Promise.all(
        newItems.map((item, index) =>
          updateReportCategory({
            id: item.id,
            name: item.name,
            order: index + 1,
          }).unwrap()
        )
      );
      api.success({
        message: t("reports.success-title"),
        description: t("Report categories reordered successfully"),
      });
    } catch (error) {
      api.error({
        message: t("reports.error-title"),
        description: handleRequestError(error, t("reports.error")),
      });
    }
  };

  return (
    <>
      {contextHolder}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            columns={columns}
            dataSource={items}
            rowKey={(record) => record.id}
            pagination={false}
            className="mt-5"
            components={{
              body: { row: DraggableRow },
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  );
};

export default ReportCategoriesTable;
